import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Calendar, MessageSquare, Settings,
  LogOut, Plus, Send, RefreshCw, Menu, X, Shield, MapPin, Phone, Mail,
  ClipboardList, AlertCircle, CheckCircle2, Clock, Search,
  LifeBuoy, Lock, User, ChevronRight, ChevronLeft, Bell, Trash2, FileText, Paperclip,
  ChevronDown, ChevronUp, Eye, Banknote, AlertTriangle, CheckCheck, Heart, Sparkles, ExternalLink, Gift
} from "lucide-react";
import { syndicatApi, chatApi, ticketApi, uploadTicketFile, pharmacieApi } from "./services/api";
import logoHorizontale from "./assets/logo/logo-horizontale.svg";
import logoMobile from "./assets/logo/logo-mobile.svg";
import waveQr from "./assets/wave/wave_qr-1.png";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

// ─── Constantes ────────────────────────────────────────────────────────────────

const BASE_WS_URL = process.env.REACT_APP_API_URL?.replace("/api/v1", "") || "http://localhost:8080";

const MENU_ITEMS = [
  { id: "dashboard",  label: "Tableau de bord",   icon: LayoutDashboard },
  { id: "reseau",     label: "Gestion du réseau", icon: Building2 },
  { id: "gardes",     label: "Planning de garde", icon: Calendar },
  // { id: "messages",   label: "Messagerie",         icon: MessageSquare },
  { id: "support",    label: "Support",            icon: LifeBuoy },
  { id: "parametres", label: "Paramètres",         icon: Settings },
];

// ─── Badges ────────────────────────────────────────────────────────────────────

const StatutBadge = ({ statut }) => {
  const map = {
    ACTIVE:     { label: "Active",     cls: "bg-green-100 text-green-700 border-green-200" },
    VALIDEE:    { label: "Validée",    cls: "bg-blue-100 text-blue-700 border-blue-200" },
    EN_ATTENTE: { label: "En attente", cls: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    SUSPENDUE:  { label: "Suspendue",  cls: "bg-red-100 text-red-700 border-red-200" },
    REJETEE:    { label: "Rejetée",    cls: "bg-red-100 text-red-700 border-red-200" },
    FERMEE:     { label: "Fermée",     cls: "bg-gray-100 text-gray-600 border-gray-200" },
    ACTIF:      { label: "Actif",      cls: "bg-green-100 text-green-700 border-green-200" },
    VALIDE:     { label: "Validé",     cls: "bg-blue-100 text-blue-700 border-blue-200" },
    SOUMIS:     { label: "Soumis",     cls: "bg-orange-100 text-orange-700 border-orange-200" },
    PUBLIE:     { label: "Publié ✓",   cls: "bg-green-100 text-green-700 border-green-200" },
    BROUILLON:  { label: "Brouillon",  cls: "bg-gray-100 text-gray-600 border-gray-200" },
    EXPIRE:     { label: "Expiré",     cls: "bg-red-100 text-red-700 border-red-200" },
  };
  const s = map[statut] || { label: statut, cls: "bg-gray-100 text-gray-600 border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const TicketBadge = ({ statut }) => {
  const map = {
    OUVERT:   { label: "Ouvert",   cls: "bg-blue-100 text-blue-700" },
    EN_COURS: { label: "En cours", cls: "bg-yellow-100 text-yellow-700" },
    RESOLU:   { label: "Résolu",   cls: "bg-green-100 text-green-700" },
    FERME:    { label: "Fermé",    cls: "bg-gray-100 text-gray-600" },
  };
  const s = map[statut] || { label: statut, cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

// ─── Pagination ────────────────────────────────────────────────────────────────

const SYN_PAGE_SIZE = 10;
const SynPagination = ({ total, page, setPage }) => {
  const totalPages = Math.ceil(total / SYN_PAGE_SIZE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
      <p className="text-xs text-gray-500">{total} élément{total > 1 ? "s" : ""} · Page {page} / {totalPages}</p>
      <div className="flex gap-2">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
          className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
          <ChevronLeft size={12} /> Préc.
        </button>
        <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
          className="flex items-center gap-1 px-3 py-1 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
          Suiv. <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
};

// ─── Composant principal ────────────────────────────────────────────────────────

export default function SyndicatDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Data
  const [profil, setProfil] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit pharmacie (réseau syndicat)
  const [showEditPharmacieModal, setShowEditPharmacieModal] = useState(false);
  const [editPharmacieData, setEditPharmacieData] = useState({});
  const [editPharmacieLoading, setEditPharmacieLoading] = useState(false);

  // Pagination
  const [pharmaciePage, setPharmaciePage] = useState(1);
  const [planningPage, setPlanningPage] = useState(1);

  // Demande ajout pharmacie (remplace "Assigner")
  const [showDemandePharmacieModal, setShowDemandePharmacieModal] = useState(false);
  const [demandeForm, setDemandeForm] = useState({
    nom: "", adresse: "", telephone: "", email: "", quartier: "", commune: "",
    numeroOrdre: "", numeroAgrement: "", latitude: "", longitude: "",
    description: "", services: "", accepteCommandes: true, proposeLivraison: false,
    pharmacienNom: "", pharmacienPrenom: "", pharmacienTelephone: "", pharmacienEmail: "", pharmacienNumeroOrdre: "",
    message: ""
  });
  const SYN_JOURS = ["lundi","mardi","mercredi","jeudi","vendredi","samedi","dimanche"];
  const SYN_JOURS_LABELS = ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"];
  const [demandeHoraires, setDemandeHoraires] = useState(() =>
    Object.fromEntries(SYN_JOURS.map(j => [j, { ouverture: "08:00", fermeture: "20:00", ferme: false }]))
  );
  const [demandeLoading, setDemandeLoading] = useState(false);
  const [demandeMsg, setDemandeMsg] = useState(null);

  // Planning CRUD
  const [showCreatePlanningModal, setShowCreatePlanningModal] = useState(false);
  const [planningForm, setPlanningForm] = useState({ titre: "", description: "", dateDebut: "", dateFin: "" });
  const [planningLoading, setPlanningLoading] = useState(false);
  const [planningSelectedPharmacies, setPlanningSelectedPharmacies] = useState(new Set());
  const [planningFilterVille, setPlanningFilterVille] = useState("all");
  const [expandedPlanningId, setExpandedPlanningId] = useState(null);
  const [planningDetail, setPlanningDetail] = useState(null);
  const [planningDetailLoading, setPlanningDetailLoading] = useState(false);
  const [showAddGardeModal, setShowAddGardeModal] = useState(false);
  const [currentPlanningId, setCurrentPlanningId] = useState(null);
  const [gardeForm, setGardeForm] = useState({ pharmacieId: "", dateGarde: "", typeGarde: "JOURNEE", heureDebut: "08:00", heureFin: "20:00", notes: "" });
  const [gardeLoading, setGardeLoading] = useState(false);
  const [planningSearch, setPlanningSearch] = useState("");
  const [planningStatutFilter, setPlanningStatutFilter] = useState("TOUS");
  // Edit planning
  const [showEditPlanningModal, setShowEditPlanningModal] = useState(false);
  const [editPlanningData, setEditPlanningData] = useState({ id: "", titre: "", description: "", dateDebut: "", dateFin: "" });
  const [editPlanningLoading, setEditPlanningLoading] = useState(false);
  const [editPlanningSelectedPharmacies, setEditPlanningSelectedPharmacies] = useState(new Set());
  const [editPlanningCurrentGardes, setEditPlanningCurrentGardes] = useState([]);
  const [editPlanningDetailLoading, setEditPlanningDetailLoading] = useState(false);

  // Réseau tabs
  const [reseauActiveTab, setReseauActiveTab] = useState("pharmacies");
  const [reseauSearch, setReseauSearch] = useState("");
  const [reseauStatutFilter, setReseauStatutFilter] = useState("tous");

  // Planning month navigation
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));

  // Ticket
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({ sujet: "", message: "", priorite: "NORMALE", categorie: "GENERAL" });
  const [ticketFile, setTicketFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Comptes associés
  const [comptes, setComptes] = useState([]);
  const [showResetCompteModal, setShowResetCompteModal] = useState(false);
  const [selectedCompte, setSelectedCompte] = useState(null);
  const [resetCompteForm, setResetCompteForm] = useState({ nouveauMotDePasse: "", confirm: "" });
  const [resetCompteLoading, setResetCompteLoading] = useState(false);
  const [resetCompteMsg, setResetCompteMsg] = useState(null);

  // Paramètres
  const [pwForm, setPwForm] = useState({ ancienMotDePasse: "", nouveauMotDePasse: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // Chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [chatConnected, setChatConnected] = useState(false);
  const stompClient = useRef(null);
  const chatEndRef = useRef(null);

  const userName = localStorage.getItem("userName") || "Syndicat";
  const syndicatId = localStorage.getItem("syndicatId");
  const initials = userName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
  const roomId = `syndicat-${syndicatId}`;

  useEffect(() => {
    loadData();
    connectWebSocket();
    return () => { stompClient.current?.deactivate(); };
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function loadData() {
    setLoading(true); setError(null);
    try {
      const [profilData, pharmaciesData] = await Promise.all([
        syndicatApi.getMe(), syndicatApi.getMyPharmacies(),
      ]);
      setProfil(profilData);
      setPharmacies(pharmaciesData || []);
      if (syndicatId) {
        try { setPlannings((await syndicatApi.getPlannings(syndicatId)) || []); } catch { setPlannings([]); }
      }
      try { setComptes((await syndicatApi.getMyComptes()) || []); } catch { setComptes([]); }
      try { setTickets((await ticketApi.getMesTickets()) || []); } catch { setTickets([]); }
      try { setMessages((await chatApi.getHistory(roomId)) || []); } catch { setMessages([]); }
    } catch (err) { setError(err.message || "Erreur de chargement"); }
    finally { setLoading(false); }
  }

  async function soumettreDemandeAjoutPharmacie(e) {
    e.preventDefault();
    if (!demandeForm.nom.trim()) return;
    setDemandeLoading(true);
    setDemandeMsg(null);
    try {
      const horairesFormatted = SYN_JOURS.map((j, i) => {
        const h = demandeHoraires[j];
        return h.ferme ? `${SYN_JOURS_LABELS[i]}: Fermé` : `${SYN_JOURS_LABELS[i]}: ${h.ouverture} - ${h.fermeture}`;
      }).join("\n");
      const msgComplet = [
        "=== PHARMACIE ===",
        `Nom: ${demandeForm.nom}`,
        `Description: ${demandeForm.description || "—"}`,
        `Services: ${demandeForm.services || "—"}`,
        "",
        "=== LOCALISATION ===",
        `Commune: ${demandeForm.commune}`,
        `Quartier: ${demandeForm.quartier}`,
        `Adresse: ${demandeForm.adresse}`,
        `Latitude: ${demandeForm.latitude || "—"}`,
        `Longitude: ${demandeForm.longitude || "—"}`,
        "",
        "=== CONTACT ===",
        `Téléphone: ${demandeForm.telephone}`,
        `Email: ${demandeForm.email}`,
        "",
        "=== DOCUMENTS ===",
        `N° Ordre: ${demandeForm.numeroOrdre}`,
        `N° Agrément Ministère: ${demandeForm.numeroAgrement}`,
        "",
        "=== CONFIGURATION ===",
        `Accepte commandes: ${demandeForm.accepteCommandes ? "Oui" : "Non"}`,
        `Propose livraison: ${demandeForm.proposeLivraison ? "Oui" : "Non"}`,
        "",
        "=== HORAIRES ===",
        horairesFormatted,
        "",
        "=== PHARMACIEN PROPRIÉTAIRE ===",
        `Nom: ${demandeForm.pharmacienNom} ${demandeForm.pharmacienPrenom}`,
        `Téléphone: ${demandeForm.pharmacienTelephone}`,
        `Email: ${demandeForm.pharmacienEmail}`,
        `N° Ordre Pharmacien: ${demandeForm.pharmacienNumeroOrdre}`,
        "",
        "=== NOTES ===",
        demandeForm.message,
      ].join("\n");
      await ticketApi.create({
        sujet: `[DEMANDE PHARMACIE] ${demandeForm.nom}`,
        message: msgComplet,
        priorite: "HAUTE",
        categorie: "GENERAL"
      });
      setDemandeMsg("Demande envoyée au Super Admin avec succès !");
      const resetForm = { nom:"", adresse:"", telephone:"", email:"", quartier:"", commune:"", numeroOrdre:"", numeroAgrement:"", latitude:"", longitude:"", description:"", services:"", accepteCommandes:true, proposeLivraison:false, pharmacienNom:"", pharmacienPrenom:"", pharmacienTelephone:"", pharmacienEmail:"", pharmacienNumeroOrdre:"", message:"" };
      const resetHoraires = Object.fromEntries(SYN_JOURS.map(j => [j, { ouverture:"08:00", fermeture:"20:00", ferme:false }]));
      setTimeout(() => { setShowDemandePharmacieModal(false); setDemandeForm(resetForm); setDemandeHoraires(resetHoraires); setDemandeMsg(null); }, 2000);
    } catch (err) { setDemandeMsg("Erreur : " + (err.message || "inconnue")); }
    finally { setDemandeLoading(false); }
  }

  async function soumettreTicket(e) {
    e.preventDefault();
    if (!ticketForm.sujet.trim() || !ticketForm.message.trim()) return;
    setTicketSubmitting(true);
    try {
      let pieceJointeNom = null;
      let pieceJointeUrl = null;
      if (ticketFile) {
        setFileUploading(true);
        try {
          const uploaded = await uploadTicketFile(ticketFile);
          pieceJointeNom = uploaded.nom;
          pieceJointeUrl = uploaded.url;
        } catch { alert("Fichier joint non uploadé, ticket envoyé sans pièce jointe"); }
        finally { setFileUploading(false); }
      }
      await ticketApi.create({ ...ticketForm, pieceJointeNom, pieceJointeUrl });
      setShowTicketForm(false);
      setTicketForm({ sujet: "", message: "", priorite: "NORMALE", categorie: "GENERAL" });
      setTicketFile(null);
      setTickets((await ticketApi.getMesTickets()) || []);
    } catch (err) { alert(err.message || "Erreur lors de l'envoi"); }
    finally { setTicketSubmitting(false); }
  }

  // Génère les dates entre début et fin inclus
  function getDatesRange(debut, fin) {
    const dates = [];
    let d = new Date(debut);
    const end = new Date(fin);
    while (d <= end) {
      dates.push(d.toISOString().slice(0, 10));
      d.setDate(d.getDate() + 1);
    }
    return dates;
  }

  function fmtDate(d) {
    if (!d) return "—";
    return new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  }

  async function openEditPlanningModal(plan) {
    setEditPlanningData({ id: plan.id, titre: plan.titre || "", description: plan.description || "", dateDebut: plan.dateDebut || "", dateFin: plan.dateFin || "" });
    setShowEditPlanningModal(true);
    setEditPlanningDetailLoading(true);
    setEditPlanningCurrentGardes([]);
    setEditPlanningSelectedPharmacies(new Set());
    try {
      const detail = await syndicatApi.getPlanningDetail(plan.id);
      const gardes = detail?.gardes || [];
      setEditPlanningCurrentGardes(gardes);
      setEditPlanningSelectedPharmacies(new Set(gardes.map(g => g.pharmacie?.id).filter(Boolean)));
    } catch { /* ignore */ }
    finally { setEditPlanningDetailLoading(false); }
  }

  async function submitEditPlanning(e) {
    e.preventDefault();
    setEditPlanningLoading(true);
    try {
      await syndicatApi.updatePlanning(editPlanningData.id, { titre: editPlanningData.titre, description: editPlanningData.description, dateDebut: editPlanningData.dateDebut, dateFin: editPlanningData.dateFin });

      const currentIds = new Set(editPlanningCurrentGardes.map(g => g.pharmacie?.id).filter(Boolean));
      const toAdd = [...editPlanningSelectedPharmacies].filter(id => !currentIds.has(id));
      const toRemove = editPlanningCurrentGardes.filter(g => g.pharmacie?.id && !editPlanningSelectedPharmacies.has(g.pharmacie.id));

      const failures = [];
      for (const garde of toRemove) {
        try { await syndicatApi.deleteGarde(garde.id); }
        catch (err) { failures.push(`Retrait de ${garde.pharmacie?.nom || "pharmacie"} : ${err.message || "erreur inconnue"}`); }
      }
      if (toAdd.length > 0) {
        const gardes = toAdd.map(pharmacieId => {
          const pharma = pharmacies.find(p => p.id === pharmacieId);
          return { pharmacieId, dateGarde: editPlanningData.dateDebut, typeGarde: "JOUR_ET_NUIT", communeId: pharma?.communeId || null, departementId: pharma?.departementId || null, notes: "" };
        });
        try { await syndicatApi.addGardesBatch(editPlanningData.id, { gardes }); }
        catch (err) { failures.push(`Ajout de pharmacie(s) : ${err.message || "erreur inconnue"}`); }
      }

      setShowEditPlanningModal(false);
      setPlannings((await syndicatApi.getPlannings(syndicatId)) || []);
      if (expandedPlanningId === editPlanningData.id) {
        setPlanningDetail(await syndicatApi.getPlanningDetail(editPlanningData.id));
      }
      if (failures.length > 0) {
        alert("Certaines modifications n'ont pas pu être enregistrées :\n\n" + failures.join("\n"));
      }
    } catch (err) { alert("Erreur : " + (err.message || "inconnue")); }
    finally { setEditPlanningLoading(false); }
  }

  const handleEditPharmacieSyndicat = (p) => {
    setEditPharmacieData({ id: p.id, nom: p.nom || "", adresseComplete: p.adresseComplete || p.adresse || "", telephone: p.telephone || "", email: p.email || "" });
    setShowEditPharmacieModal(true);
  };
  const submitEditPharmacieSyndicat = async (e) => {
    e.preventDefault();
    setEditPharmacieLoading(true);
    try {
      await pharmacieApi.update(editPharmacieData.id, editPharmacieData);
      setShowEditPharmacieModal(false);
      const data = await syndicatApi.getMyPharmacies(syndicatId);
      setPharmacies(Array.isArray(data) ? data : data?.content || []);
    } catch (err) { alert("Erreur : " + (err.message || "inconnue")); }
    finally { setEditPharmacieLoading(false); }
  };

  async function creerPlanning(publier = false) {
    if (!planningForm.dateDebut || !planningForm.dateFin) return;
    const titre = planningForm.titre.trim() || `Semaine du ${planningForm.dateDebut} au ${planningForm.dateFin}`;
    setPlanningLoading(true);
    try {
      const created = await syndicatApi.createPlanning(syndicatId, { ...planningForm, titre });
      const pid = created?.id || created?.data?.id;

      // Une garde par pharmacie pour toute la semaine (plus de per-day)
      if (pid && planningSelectedPharmacies.size > 0) {
        const gardes = [];
        for (const pharmacieId of planningSelectedPharmacies) {
          const pharmacie = pharmacies.find(p => p.id === pharmacieId);
          gardes.push({
            pharmacieId,
            dateGarde: planningForm.dateDebut,
            typeGarde: "JOUR_ET_NUIT",
            communeId: pharmacie?.communeId || null,
            departementId: pharmacie?.departementId || null,
            notes: ""
          });
        }
        try { await syndicatApi.addGardesBatch(pid, { gardes }); } catch (err) { console.warn("Batch gardes:", err); }
      }

      // Auto-publier si demandé OU si le planning couvre la semaine en cours
      const today = new Date().toISOString().slice(0, 10);
      const couvreAujourdhui = planningForm.dateDebut <= today && planningForm.dateFin >= today;
      if ((publier || couvreAujourdhui) && pid) {
        // Le backend gère la chaîne BROUILLON → EN_VALIDATION → VALIDE → PUBLIE
        try { await syndicatApi.publierPlanning(pid); } catch (err) { console.warn("Publication:", err.message); }
      }

      setShowCreatePlanningModal(false);
      setPlanningForm({ titre: "", description: "", dateDebut: "", dateFin: "" });
      setPlanningSelectedPharmacies(new Set());
      setPlanningFilterVille("all");
      setPlannings((await syndicatApi.getPlannings(syndicatId)) || []);
    } catch (err) { alert(err.message || "Erreur lors de la création"); }
    finally { setPlanningLoading(false); }
  }

  async function publierPlanningComplet(planningId) {
    try {
      // Le backend gère la chaîne BROUILLON → EN_VALIDATION → VALIDE → PUBLIE
      await syndicatApi.publierPlanning(planningId);
      setPlannings((await syndicatApi.getPlannings(syndicatId)) || []);
    } catch (err) { alert("Erreur lors de la publication : " + (err.message || "inconnue")); }
  }

  async function suspendrePharmacieReseau(pharmacieId, nom) {
    if (!window.confirm(`Suspendre "${nom}" pour non-paiement ? Elle n'apparaîtra plus dans les plannings.`)) return;
    try {
      await pharmacieApi.suspendre(pharmacieId, "Non-paiement");
      setPharmacies(prev => prev.map(p => p.id === pharmacieId ? { ...p, statut: "SUSPENDUE" } : p));
    } catch (err) { alert(err.message || "Erreur"); }
  }

  async function activerPharmacieReseau(pharmacieId) {
    try {
      await pharmacieApi.activer(pharmacieId);
      setPharmacies(prev => prev.map(p => p.id === pharmacieId ? { ...p, statut: "ACTIVE" } : p));
    } catch (err) { alert(err.message || "Erreur"); }
  }

  async function togglePlanningDetail(planningId) {
    if (expandedPlanningId === planningId) { setExpandedPlanningId(null); setPlanningDetail(null); return; }
    setExpandedPlanningId(planningId);
    setPlanningDetailLoading(true);
    try { setPlanningDetail(await syndicatApi.getPlanningDetail(planningId)); }
    catch { setPlanningDetail(null); }
    finally { setPlanningDetailLoading(false); }
  }

  async function soumettreForValidation(planningId) {
    if (!window.confirm("Soumettre ce planning pour validation ?")) return;
    try { await syndicatApi.soumettreForValidation(planningId); setPlannings((await syndicatApi.getPlannings(syndicatId)) || []); }
    catch (err) { alert(err.message); }
  }

  async function validerPlanning(planningId) {
    if (!window.confirm("Valider ce planning ?")) return;
    try { await syndicatApi.validerPlanning(planningId); setPlannings((await syndicatApi.getPlannings(syndicatId)) || []); }
    catch (err) { alert(err.message); }
  }

  async function ajouterGarde(e) {
    e.preventDefault();
    if (!gardeForm.pharmacieId || !gardeForm.dateGarde) return;
    setGardeLoading(true);
    try {
      await syndicatApi.addGarde(currentPlanningId, gardeForm);
      setShowAddGardeModal(false);
      setGardeForm({ pharmacieId: "", dateGarde: "", typeGarde: "JOURNEE", heureDebut: "08:00", heureFin: "20:00", notes: "" });
      setPlanningDetail(await syndicatApi.getPlanningDetail(currentPlanningId));
    } catch (err) { alert(err.message || "Erreur"); }
    finally { setGardeLoading(false); }
  }

  async function supprimerGarde(gardeId) {
    if (!window.confirm("Supprimer cette garde ?")) return;
    try {
      await syndicatApi.deleteGarde(gardeId);
      if (expandedPlanningId) setPlanningDetail(await syndicatApi.getPlanningDetail(expandedPlanningId));
    } catch (err) { alert(err.message); }
  }

  function connectWebSocket() {
    const token = localStorage.getItem("token");
    if (!token) return;
    const client = new Client({
      webSocketFactory: () => new SockJS(`${BASE_WS_URL}/ws`),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      onConnect: () => {
        setChatConnected(true);
        client.subscribe(`/topic/chat/${roomId}`, msg => setMessages(prev => [...prev, JSON.parse(msg.body)]));
      },
      onDisconnect: () => setChatConnected(false),
      onStompError: () => setChatConnected(false),
    });
    client.activate(); stompClient.current = client;
  }

  function sendMessage(e) {
    e.preventDefault();
    if (!newMessage.trim() || !stompClient.current?.connected) return;
    stompClient.current.publish({
      destination: `/app/chat/${roomId}`,
      body: JSON.stringify({ roomId, senderId: syndicatId, senderName: userName, content: newMessage.trim(), timestamp: new Date().toISOString(), type: "CHAT" }),
    });
    setNewMessage("");
  }

  async function changerMotDePasse(e) {
    e.preventDefault();
    if (pwForm.nouveauMotDePasse !== pwForm.confirm) {
      setPwMsg({ type: "error", text: "Les mots de passe ne correspondent pas" }); return;
    }
    setPwLoading(true); setPwMsg(null);
    try {
      await syndicatApi.changePassword({ ancienMotDePasse: pwForm.ancienMotDePasse, nouveauMotDePasse: pwForm.nouveauMotDePasse, confirmationMotDePasse: pwForm.confirm });
      setPwMsg({ type: "success", text: "Mot de passe modifié avec succès" });
      setPwForm({ ancienMotDePasse: "", nouveauMotDePasse: "", confirm: "" });
    } catch (err) { setPwMsg({ type: "error", text: err.message || "Erreur" }); }
    finally { setPwLoading(false); }
  }

  async function handleResetComptePassword(e) {
    e.preventDefault();
    if (resetCompteForm.nouveauMotDePasse !== resetCompteForm.confirm) {
      setResetCompteMsg({ type: "error", text: "Les mots de passe ne correspondent pas" }); return;
    }
    if (resetCompteForm.nouveauMotDePasse.length < 6) {
      setResetCompteMsg({ type: "error", text: "Minimum 6 caractères" }); return;
    }
    setResetCompteLoading(true); setResetCompteMsg(null);
    try {
      await syndicatApi.resetComptePassword(selectedCompte.id, resetCompteForm.nouveauMotDePasse);
      setResetCompteMsg({ type: "success", text: "Mot de passe réinitialisé avec succès" });
      setResetCompteForm({ nouveauMotDePasse: "", confirm: "" });
      setTimeout(() => { setShowResetCompteModal(false); setResetCompteMsg(null); }, 1500);
    } catch (err) { setResetCompteMsg({ type: "error", text: err.message || "Erreur" }); }
    finally { setResetCompteLoading(false); }
  }

  const ticketsOuverts = tickets.filter(t => t.statut === "OUVERT" || t.statut === "EN_COURS").length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-20 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-30 transition-all duration-300 flex flex-col shadow-sm
        ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full md:translate-x-0 md:w-16'}`}>
        {/* Logo */}
        <div className={`flex items-center border-b border-gray-100 h-16 flex-shrink-0 ${sidebarOpen ? 'px-5' : 'px-3 justify-center'}`}>
          {sidebarOpen ? (
            <div>
              <img src={logoHorizontale} alt="SunuFarmasi" className="h-8 w-auto" />
              <span className="text-[10px] text-emerald-600 font-semibold tracking-wider uppercase">Espace Syndicat</span>
            </div>
          ) : (
            <img src={logoMobile} alt="SunuFarmasi" className="h-9 w-9" />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {MENU_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => { setActiveTab(item.id); if (window.innerWidth < 768) setSidebarOpen(false); }}
                title={!sidebarOpen ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm font-medium relative
                  ${isActive ? "bg-emerald-50 text-emerald-700" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"}
                  ${!sidebarOpen ? 'justify-center' : ''}`}
              >
                {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-600 rounded-r-full" />}
                <Icon size={18} className="flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.id === "messages" && (
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${chatConnected ? "bg-emerald-500" : "bg-gray-300"}`} />
                    )}
                    {item.id === "support" && ticketsOuverts > 0 && (
                      <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{ticketsOuverts}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer sidebar */}
        <div className="border-t border-gray-100 p-3 space-y-2 flex-shrink-0">
          {sidebarOpen && (
            <a href="https://pay.wave.com/m/M_sn_zChNJsQ96QXG/c/sn/" target="_blank" rel="noreferrer"
              className="flex items-center gap-2.5 w-full px-3 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:opacity-90 transition-all">
              <Heart size={14} className="text-white fill-white/50 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold leading-tight">Soutenir SunuFarmasi</p>
                <p className="text-white/70 text-[10px]">Don via Wave 🌊</p>
              </div>
              <ExternalLink size={11} className="text-white/60 flex-shrink-0" />
            </a>
          )}
          <div className={`flex items-center gap-3 px-1 py-1 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{initials}</div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                {profil && <p className="text-[11px] text-gray-400 font-mono truncate">{profil.code}</p>}
              </div>
            )}
          </div>
          <button onClick={() => { localStorage.clear(); navigate("/login"); }}
            title={!sidebarOpen ? "Déconnexion" : undefined}
            className={`w-full flex items-center gap-3 px-3 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-all text-sm ${!sidebarOpen ? 'justify-center' : ''}`}>
            <LogOut size={16} className="flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`transition-all duration-300 flex flex-col min-h-screen ${sidebarOpen ? 'md:ml-64' : 'ml-0 md:ml-16'}`}>
        {/* Header */}
        <header className="bg-white sticky top-0 z-20 border-b border-gray-100 h-16 flex items-center">
          <div className="flex items-center justify-between px-3 md:px-6 w-full">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                <Menu size={20} />
              </button>
              <div>
                <h1 className="text-base font-bold text-gray-900 leading-tight">
                  {MENU_ITEMS.find(i => i.id === activeTab)?.label}
                </h1>
                {profil && (
                  <p className="text-xs text-gray-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block" />
                    {profil.nom}
                    {profil.code && <><span className="text-gray-300">·</span><span className="font-mono text-emerald-600 text-[11px]">{profil.code}</span></>}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg">
                🇸🇳 <span>Sénégal</span>
              </span>
              <button onClick={loadData} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors" title="Rafraîchir">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </button>
              <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <Bell size={18} className="text-gray-500" />
                {ticketsOuverts > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {ticketsOuverts}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6">
          {loading && (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Chargement...</p>
              </div>
            </div>
          )}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3 mb-6">
              <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
              <button onClick={loadData} className="ml-auto text-xs text-red-600 underline">Réessayer</button>
            </div>
          )}

          {!loading && (
            <>
              {/* ═══ TABLEAU DE BORD ══════════════════════════════════════════ */}
              {activeTab === "dashboard" && (
                <div className="space-y-6">
                  {/* Alerte abonnement */}
                  {profil?.abonnement?.statut === "SUSPENDU" && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                      <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                      <p className="text-red-700 text-sm font-medium">Votre compte est suspendu. Contactez l'administration.</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[
                      { label: "Pharmacies totales",    value: pharmacies.length,                                           icon: Building2, color: "green",  sub: `${pharmacies.filter(p=>p.statut==="ACTIVE").length} active(s)` },
                      { label: "En attente validation", value: pharmacies.filter(p=>p.statut==="EN_ATTENTE").length,        icon: Clock,     color: "orange", sub: "à traiter" },
                      { label: "Plannings publiés",     value: plannings.filter(p=>p.statut==="PUBLIE").length,             icon: Calendar,  color: "blue",   sub: `${plannings.length} total` },
                      { label: "Comptes associés",      value: comptes.length,                                              icon: User,      color: "purple", sub: "sous-comptes" },
                    ].map((stat, i) => {
                      const Icon = stat.icon;
                      const C = { green:{ bg:"bg-green-600" }, orange:{ bg:"bg-orange-600" }, blue:{ bg:"bg-blue-600" }, purple:{ bg:"bg-purple-600" } }[stat.color];
                      return (
                        <div key={i} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                          <div className="flex items-center justify-between mb-4">
                            <div className={`w-12 h-12 ${C.bg} rounded-lg flex items-center justify-center`}><Icon className="text-white" size={22} /></div>
                            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{stat.sub}</span>
                          </div>
                          <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                          <p className="text-sm text-gray-600">{stat.label}</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Bannière de don ── */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-green-600 via-teal-600 to-emerald-700 shadow-lg">
                    {/* Cercles décoratifs */}
                    <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/5 rounded-full" />
                    <div className="absolute -bottom-14 -left-8 w-56 h-56 bg-white/5 rounded-full" />
                    <div className="absolute top-4 right-40 w-20 h-20 bg-white/5 rounded-full" />

                    <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                      {/* Icône */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-3">
                        <div className="w-20 h-20 bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl flex items-center justify-center shadow-inner">
                          <Heart size={36} className="text-white fill-white/40" />
                        </div>
                        <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
                          <Sparkles size={12} className="text-yellow-300" />
                          <span className="text-white text-xs font-semibold">Wave Pay</span>
                        </div>
                      </div>

                      {/* Texte */}
                      <div className="flex-1 text-center md:text-left">
                        <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                          <Gift size={16} className="text-yellow-300" />
                          <span className="text-yellow-300 text-xs font-bold uppercase tracking-widest">Soutenir SunuFarmasi</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-2">
                          Votre don fait la différence
                        </h2>
                        <p className="text-white/80 text-sm md:text-base leading-relaxed max-w-lg">
                          SunuFarmasi connecte chaque jour des milliers de Sénégalais avec leurs pharmacies.
                          Aidez-nous à maintenir et améliorer ce service pour tous. <span className="text-yellow-300 font-semibold">Chaque franc compte.</span>
                        </p>

                        {/* Stats symboliques */}
                        <div className="flex items-center justify-center md:justify-start gap-6 mt-4">
                          {[
                            { label: "Pharmacies connectées", val: "147+" },
                            { label: "Syndicats partenaires", val: "5+" },
                            { label: "Patients servis", val: "10K+" },
                          ].map(({ label, val }) => (
                            <div key={label} className="text-center">
                              <p className="text-white font-bold text-lg leading-none">{val}</p>
                              <p className="text-white/60 text-xs mt-0.5">{label}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Bouton CTA + QR */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-3">
                        <a
                          href="https://pay.wave.com/m/M_sn_zChNJsQ96QXG/c/sn/"
                          target="_blank"
                          rel="noreferrer"
                          className="group flex items-center gap-3 px-7 py-4 bg-white text-green-700 font-extrabold text-base rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 active:scale-95"
                        >
                          <span className="text-2xl">🌊</span>
                          <span>Faire un don</span>
                          <ExternalLink size={15} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                        </a>
                        {/* QR Code Wave */}
                        <div className="bg-white rounded-xl p-2 shadow-md">
                          <img src={waveQr} alt="QR Wave" className="w-24 h-24 object-contain rounded-lg" />
                          <p className="text-center text-xs text-gray-500 mt-1 font-medium">Scanner avec Wave</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {profil && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2"><Shield size={18} className="text-green-600" /><span>Mon syndicat</span></h3>
                        <div className="space-y-3 text-sm">
                          {[
                            { icon: MapPin, val: `${profil.zone?.nomCommune || profil.zone?.nomRegion || "—"} · ${profil.zone?.nomRegion || "—"}` },
                            { icon: Phone, val: profil.contact?.telephone || "—" },
                            { icon: Mail, val: profil.contact?.email || "—" },
                            { icon: ClipboardList, val: `Responsable: ${profil.responsable?.nom || "—"}` },
                          ].map(({ icon: Icon, val }, i) => (
                            <div key={i} className="flex items-center space-x-2 text-gray-600">
                              <Icon size={14} className="text-green-500 flex-shrink-0" /><span>{val}</span>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => setActiveTab("parametres")} className="mt-4 text-xs text-green-600 hover:underline flex items-center space-x-1">
                          <span>Modifier le profil</span><ChevronRight size={12} />
                        </button>
                      </div>
                    )}

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">Pharmacies récentes</h3>
                        <button onClick={() => setActiveTab("reseau")} className="text-sm text-green-600 hover:text-green-700 font-medium">Voir tout →</button>
                      </div>
                      {pharmacies.length === 0 ? (
                        <div className="text-center py-6">
                          <Building2 size={32} className="text-gray-300 mx-auto mb-2" />
                          <p className="text-sm text-gray-400">Aucune pharmacie</p>
                          <button onClick={() => setShowDemandePharmacieModal(true)} className="mt-2 text-xs text-green-600 underline">Demander l'ajout d'une pharmacie</button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {pharmacies.slice(0, 4).map(p => (
                            <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div><p className="font-medium text-sm text-gray-900">{p.nom}</p><p className="text-xs text-gray-500">{p.adresseComplete || p.communeNom || "—"}</p></div>
                              <StatutBadge statut={p.statut} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">Tickets support</h3>
                        <button onClick={() => setShowTicketForm(true)} className="text-sm text-green-600 hover:text-green-700 font-medium">+ Nouveau</button>
                      </div>
                      {tickets.length === 0 ? (
                        <div className="text-center py-6"><LifeBuoy size={32} className="text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">Aucun ticket</p></div>
                      ) : (
                        <div className="space-y-3">
                          {tickets.slice(0, 4).map(t => (
                            <div key={t.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" onClick={() => setSelectedTicket(t)}>
                              <div className="flex-1 min-w-0"><p className="font-medium text-sm text-gray-900 truncate">{t.sujet}</p><p className="text-xs text-gray-400">{t.categorie}</p></div>
                              <TicketBadge statut={t.statut} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Planning actuel */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2"><Calendar size={18} className="text-blue-600" /><span>Plannings actifs</span></h3>
                      <button onClick={() => setActiveTab("gardes")} className="text-sm text-green-600 hover:text-green-700 font-medium">Voir tout →</button>
                    </div>
                    {plannings.length === 0 ? (
                      <div className="text-center py-6">
                        <Calendar size={32} className="text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Aucun planning</p>
                        <button onClick={() => setActiveTab("gardes")} className="mt-2 text-xs text-blue-600 underline">Créer un planning</button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {plannings.slice(0, 3).map(plan => (
                          <div key={plan.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-sm text-gray-900">{plan.titre || `Planning #${plan.id?.slice(0,8)}`}</p>
                              <p className="text-xs text-gray-500">{plan.dateDebut} → {plan.dateFin} · {plan.nombreGardes || 0} gardes</p>
                            </div>
                            <StatutBadge statut={plan.statut} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 mb-6">Actions rapides</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: "Demander", sub: "Ajout pharmacie", color: "green", icon: Plus, action: () => { setShowDemandePharmacieModal(true); } },
                        { label: "Plannings", sub: "Gardes & Schedules", color: "orange", icon: Calendar, action: () => setActiveTab("gardes") },
                        { label: "Messagerie", sub: "Contacter l'admin", color: "blue", icon: MessageSquare, action: () => setActiveTab("messages") },
                        { label: "Faire un don", sub: "Soutenir SunuFarmasi", color: "teal", icon: Heart, action: () => window.open("https://pay.wave.com/m/M_sn_zChNJsQ96QXG/c/sn/", "_blank") },
                      ].map((a, i) => {
                        const Icon = a.icon;
                        const C = {
                          green:  { border:"border-green-200",  hover:"hover:bg-green-50",  icon:"bg-green-100",  text:"text-green-600"  },
                          orange: { border:"border-orange-200", hover:"hover:bg-orange-50", icon:"bg-orange-100", text:"text-orange-600" },
                          blue:   { border:"border-blue-200",   hover:"hover:bg-blue-50",   icon:"bg-blue-100",   text:"text-blue-600"   },
                          purple: { border:"border-purple-200", hover:"hover:bg-purple-50", icon:"bg-purple-100", text:"text-purple-600" },
                          teal:   { border:"border-teal-300",   hover:"hover:bg-teal-50",   icon:"bg-gradient-to-br from-teal-500 to-green-600", text:"text-white" },
                        }[a.color] || {};
                        const isTeal = a.color === "teal";
                        return (
                          <button key={i} onClick={a.action}
                            className={`flex items-center space-x-4 p-4 border-2 border-dashed ${C.border} rounded-lg ${C.hover} transition-all ${isTeal ? "relative overflow-hidden" : ""}`}>
                            {isTeal && <div className="absolute inset-0 bg-gradient-to-r from-teal-50 to-green-50 opacity-60 pointer-events-none" />}
                            <div className={`relative z-10 w-12 h-12 ${C.icon} rounded-lg flex items-center justify-center shadow-sm`}>
                              <Icon className={C.text} size={22} />
                            </div>
                            <div className="relative z-10 text-left">
                              <p className={`font-semibold ${isTeal ? "text-teal-700" : "text-gray-900"}`}>{a.label}</p>
                              <p className="text-xs text-gray-600">{a.sub}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ GESTION DU RÉSEAU ════════════════════════════════════════ */}
              {activeTab === "reseau" && (() => {
                const pharmaciens = pharmacies
                  .filter(p => p.pharmacienProprietaireNom)
                  .map(p => ({ id: p.id + "_ph", nom: p.pharmacienProprietaireNom, pharmacie: p.nom, pharmacieId: p.id, telephone: p.telephone, commune: p.communeNom, statut: p.statut }));
                const pharmaciensAvec = pharmacies.filter(p => p.pharmacienProprietaireNom).length;
                const pharmaciensSans = pharmacies.length - pharmaciensAvec;
                return (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900">Gestion du Réseau</h2>
                      <p className="text-sm text-gray-500">Pharmacies et Pharmaciens</p>
                    </div>
                    <button onClick={() => setShowDemandePharmacieModal(true)} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">
                      <Plus size={16} /><span>Demander l'ajout</span>
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: "Pharmacies", val: pharmacies.length, icon: Building2, iconCls: "text-white", bgCls: "bg-green-600" },
                      { label: "Pharmaciens", val: pharmaciens.length, icon: User, iconCls: "text-white", bgCls: "bg-blue-600" },
                      { label: "Assignées", val: pharmaciensAvec, icon: CheckCircle2, iconCls: "text-white", bgCls: "bg-green-500" },
                      { label: "Sans pharmacien", val: pharmaciensSans, icon: AlertCircle, iconCls: "text-white", bgCls: "bg-gray-400" },
                    ].map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                            <p className="text-3xl font-bold text-gray-900">{s.val}</p>
                          </div>
                          <div className={`w-12 h-12 ${s.bgCls} rounded-xl flex items-center justify-center`}>
                            <Icon size={22} className={s.iconCls} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Card with underline tabs */}
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* Underline tabs */}
                    <div className="border-b border-gray-200 px-4 overflow-x-auto">
                      <div className="flex gap-1 min-w-max">
                        {[
                          { key: "pharmacies", label: "Pharmacies", count: pharmacies.length },
                          { key: "pharmaciens", label: "Pharmaciens", count: pharmaciens.length },
                          { key: "comptes", label: "Comptes associés", count: comptes.length },
                        ].map(tab => (
                          <button key={tab.key} onClick={() => { setReseauActiveTab(tab.key); setReseauSearch(""); }}
                            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                              reseauActiveTab === tab.key
                                ? "border-green-600 text-green-700"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}>
                            {tab.label}
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${reseauActiveTab === tab.key ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                              {tab.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Search + filter bar */}
                    <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                      <div className="relative flex-1 min-w-48">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input type="text" value={reseauSearch} onChange={e => setReseauSearch(e.target.value)}
                          placeholder={reseauActiveTab === "pharmacies" ? "Rechercher une pharmacie..." : reseauActiveTab === "pharmaciens" ? "Rechercher un pharmacien..." : "Rechercher un compte..."}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                      {reseauActiveTab === "pharmacies" && (
                        <select value={reseauStatutFilter} onChange={e => setReseauStatutFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
                          <option value="tous">Tous les statuts</option>
                          <option value="ACTIVE">Actives</option>
                          <option value="EN_ATTENTE">En attente</option>
                          <option value="SUSPENDUE">Suspendues</option>
                          <option value="VALIDEE">Validées</option>
                        </select>
                      )}
                    </div>

                    {/* Pharmacies tab */}
                    {reseauActiveTab === "pharmacies" && (() => {
                      const filtered = pharmacies.filter(p => {
                        const matchSearch = !reseauSearch || p.nom?.toLowerCase().includes(reseauSearch.toLowerCase());
                        const matchStatut = reseauStatutFilter === "tous" || p.statut === reseauStatutFilter;
                        return matchSearch && matchStatut;
                      });
                      if (filtered.length === 0) return (
                        <div className="p-12 text-center">
                          <Building2 size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 font-medium">Aucune pharmacie trouvée</p>
                          {pharmacies.length === 0 && <button onClick={() => setShowDemandePharmacieModal(true)} className="mt-4 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">Demander l'ajout</button>}
                        </div>
                      );
                      return (
                        <>
                          <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[640px]">

                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>{["Pharmacie", "Ville", "Pharmacien", "Contact", "Statut", "Actions"].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                              ))}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {filtered.slice((pharmaciePage - 1) * SYN_PAGE_SIZE, pharmaciePage * SYN_PAGE_SIZE).map(p => (
                                <tr key={p.id} className={`hover:bg-gray-50 transition-colors ${p.statut === "SUSPENDUE" ? "bg-red-50/30" : ""}`}>
                                  <td className="px-4 py-3.5">
                                    <div className="flex items-center gap-3">
                                      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <Building2 size={16} className="text-green-600" />
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-900 text-sm">{p.nom}</p>
                                        <p className="text-xs text-gray-400">{p.adresseComplete || "—"}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5">
                                    <p className="flex items-center gap-1 text-sm text-gray-600"><MapPin size={12} className="text-gray-400" />{p.communeNom || "—"}</p>
                                  </td>
                                  <td className="px-4 py-3.5">
                                    {p.pharmacienProprietaireNom ? (
                                      <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                          <User size={13} className="text-blue-600" />
                                        </div>
                                        <span className="text-sm text-gray-700">{p.pharmacienProprietaireNom}</span>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                                        <User size={13} />Assigner
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3.5 text-gray-600">
                                    {p.telephone && <p className="flex items-center gap-1 text-sm"><Phone size={12} className="text-gray-400" />{p.telephone}</p>}
                                  </td>
                                  <td className="px-4 py-3.5"><StatutBadge statut={p.statut} /></td>
                                  <td className="px-4 py-3.5">
                                    <div className="flex items-center gap-2">
                                      <button onClick={() => handleEditPharmacieSyndicat(p)}
                                        className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors" title="Modifier">
                                        ✏️
                                      </button>
                                      {p.statut === "SUSPENDUE" ? (
                                        <button onClick={() => activerPharmacieReseau(p.id)}
                                          className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors" title="Activer">
                                          <CheckCircle2 size={15} />
                                        </button>
                                      ) : p.statut === "ACTIVE" ? (
                                        <button onClick={() => suspendrePharmacieReseau(p.id, p.nom)}
                                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Suspendre">
                                          <AlertCircle size={15} />
                                        </button>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                          <SynPagination total={filtered.length} page={pharmaciePage} setPage={setPharmaciePage} />
                        </>
                      );
                    })()}

                    {/* Pharmaciens tab */}
                    {reseauActiveTab === "pharmaciens" && (() => {
                      const filtered = pharmaciens.filter(ph =>
                        !reseauSearch || ph.nom?.toLowerCase().includes(reseauSearch.toLowerCase()) || ph.pharmacie?.toLowerCase().includes(reseauSearch.toLowerCase())
                      );
                      if (filtered.length === 0) return (
                        <div className="p-12 text-center">
                          <User size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 font-medium">
                            {pharmaciens.length === 0 ? "Aucun pharmacien assigné à vos pharmacies" : "Aucun résultat"}
                          </p>
                        </div>
                      );
                      return (
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[560px]">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{["Pharmacien", "Pharmacie", "Ville", "Contact", "Statut"].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filtered.map(ph => (
                              <tr key={ph.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                      <User size={16} className="text-blue-600" />
                                    </div>
                                    <span className="font-semibold text-gray-900">{ph.nom}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <Building2 size={13} className="text-green-600" />
                                    </div>
                                    <span className="text-sm text-gray-700">{ph.pharmacie}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <p className="flex items-center gap-1 text-sm text-gray-600"><MapPin size={12} className="text-gray-400" />{ph.commune || "—"}</p>
                                </td>
                                <td className="px-4 py-3.5 text-gray-600">
                                  {ph.telephone && <p className="flex items-center gap-1 text-sm"><Phone size={12} className="text-gray-400" />{ph.telephone}</p>}
                                </td>
                                <td className="px-4 py-3.5"><StatutBadge statut={ph.statut} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      );
                    })()}

                    {/* Comptes tab */}
                    {reseauActiveTab === "comptes" && (() => {
                      const filtered = comptes.filter(c =>
                        !reseauSearch || c.nom?.toLowerCase().includes(reseauSearch.toLowerCase()) || c.username?.toLowerCase().includes(reseauSearch.toLowerCase())
                      );
                      if (filtered.length === 0) return (
                        <div className="p-12 text-center">
                          <User size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 font-medium">Aucun compte associé trouvé</p>
                        </div>
                      );
                      return (
                        <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[520px]">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>{["Compte", "Username", "Contact", "Statut", "Actions"].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filtered.map(c => (
                              <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3.5">
                                  <p className="font-semibold text-gray-900">{c.nom}</p>
                                  <p className="text-xs text-gray-400 font-mono">{c.code}</p>
                                </td>
                                <td className="px-4 py-3.5 text-gray-700 font-mono text-sm">{c.username || "—"}</td>
                                <td className="px-4 py-3.5 text-gray-600">
                                  {c.contact?.email && <p className="flex items-center gap-1 text-xs"><Mail size={11} />{c.contact.email}</p>}
                                  {c.contact?.telephone && <p className="flex items-center gap-1 text-xs mt-0.5"><Phone size={11} />{c.contact.telephone}</p>}
                                </td>
                                <td className="px-4 py-3.5"><StatutBadge statut={c.statut === "ACTIF" ? "ACTIF" : (c.statut || "ACTIF")} /></td>
                                <td className="px-4 py-3.5">
                                  <button onClick={() => { setSelectedCompte(c); setResetCompteForm({ nouveauMotDePasse: "", confirm: "" }); setResetCompteMsg(null); setShowResetCompteModal(true); }}
                                    className="flex items-center space-x-1 px-3 py-1.5 border border-orange-300 text-orange-600 text-xs font-medium rounded-lg hover:bg-orange-50 transition-colors">
                                    <Lock size={12} /><span>Changer mdp</span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                );
              })()}

              {/* ═══ PLANNING DE GARDE ════════════════════════════════════════ */}
              {activeTab === "gardes" && (() => {
                const today = new Date().toISOString().slice(0, 10);
                const monthStart = selectedMonth + "-01";
                const monthEnd = selectedMonth + "-31";
                const monthLabel = new Date(selectedMonth + "-15T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
                const currentMonthStr = new Date().toISOString().slice(0, 7);

                const prevMonth = () => {
                  const [y, m] = selectedMonth.split("-").map(Number);
                  const d = new Date(y, m - 2, 1);
                  setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                };
                const nextMonth = () => {
                  const [y, m] = selectedMonth.split("-").map(Number);
                  const d = new Date(y, m, 1);
                  setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
                };

                // Filter plannings that overlap with selected month
                const planningsForMonth = plannings.filter(p =>
                  p.dateDebut && p.dateFin &&
                  p.dateDebut <= monthEnd && p.dateFin >= monthStart
                );

                const enCours = plannings.filter(p => p.dateDebut <= today && p.dateFin >= today);
                const currentWeek = plannings.find(p => p.dateDebut <= today && p.dateFin >= today && p.statut === "PUBLIE");

                return (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Calendar size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900">Planning de Garde</h2>
                      <p className="text-sm text-gray-500">Gérez vos <span className="text-green-600">pharmacies de garde</span></p>
                    </div>
                    <button onClick={() => setShowCreatePlanningModal(true)} className="flex items-center space-x-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors">
                      <Plus size={16} /><span>Nouvelle Semaine</span>
                    </button>
                  </div>

                  {/* Stats cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: "Total", val: plannings.length, icon: Calendar, iconCls: "text-white", bgCls: "bg-gray-600" },
                      { label: "Publiées", val: plannings.filter(p => p.statut === "PUBLIE").length, icon: CheckCircle2, iconCls: "text-white", bgCls: "bg-green-600" },
                      { label: "Brouillons", val: plannings.filter(p => p.statut === "BROUILLON").length, icon: FileText, iconCls: "text-white", bgCls: "bg-orange-500" },
                      { label: "Actuelle", val: enCours.length, icon: Clock, iconCls: "text-white", bgCls: "bg-blue-500" },
                    ].map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                            <p className="text-3xl font-bold text-gray-900">{s.val}</p>
                          </div>
                          <div className={`w-12 h-12 ${s.bgCls} rounded-xl flex items-center justify-center`}>
                            <Icon size={22} className={s.iconCls} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Month navigation + status filter */}
                  <div className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Calendar navigator */}
                      <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-1 py-1">
                        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-600">
                          <ChevronLeft size={16} />
                        </button>
                        <span className="min-w-[160px] text-center text-sm font-semibold text-gray-800 px-2 select-none capitalize">
                          <Calendar size={13} className="inline mr-1.5 text-green-600 -mt-0.5" />{monthLabel}
                        </span>
                        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors text-gray-600">
                          <ChevronRight size={16} />
                        </button>
                      </div>
                      {selectedMonth !== currentMonthStr && (
                        <button onClick={() => setSelectedMonth(currentMonthStr)} className="px-3 py-2 text-sm rounded-lg border border-green-200 text-green-700 hover:bg-green-50 font-medium">
                          Ce mois
                        </button>
                      )}
                      <div className="flex gap-1.5 flex-wrap">
                        {[["TOUS","Tous"],["PUBLIE","Publié"],["BROUILLON","Brouillon"],["VALIDE","Validé"]].map(([v,l]) => (
                          <button key={v} onClick={() => setPlanningStatutFilter(v)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${planningStatutFilter === v ? "bg-green-600 text-white border-green-600" : "bg-white border-gray-200 text-gray-600 hover:border-green-400"}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Current week highlight */}
                  {currentWeek && (
                    <div className="bg-green-50 border-l-4 border-green-500 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-bold text-green-600 uppercase tracking-wide mb-0.5">Semaine en cours</p>
                        <p className="font-bold text-gray-900">{fmtDate(currentWeek.dateDebut)} - {fmtDate(currentWeek.dateFin)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{currentWeek.nombreGardes || 0} pharmacies de garde</p>
                      </div>
                      <button onClick={() => togglePlanningDetail(currentWeek.id)} className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700">
                        Voir les détails
                      </button>
                    </div>
                  )}

                  {/* Planning list for selected month */}
                  {(() => {
                    const filtered = planningsForMonth
                      .filter(p => planningStatutFilter === "TOUS" || p.statut === planningStatutFilter)
                      .sort((a, b) => (b.dateDebut || "").localeCompare(a.dateDebut || ""));

                    if (filtered.length === 0) return (
                      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                        <Calendar size={48} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 font-medium">
                          {plannings.length === 0
                            ? "Aucune semaine de garde créée"
                            : `Aucune semaine pour ${monthLabel}`}
                        </p>
                        <button onClick={() => setShowCreatePlanningModal(true)} className="mt-4 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700">+ Nouvelle semaine</button>
                      </div>
                    );

                    return (
                      <div className="space-y-4">
                        {filtered.map(plan => {
                          const isEnCours = plan.dateDebut <= today && plan.dateFin >= today;
                          const isAVenir = plan.dateDebut > today;
                          const isPast = plan.dateFin < today;
                          const isExpanded = expandedPlanningId === plan.id;

                          const dateBadge = isEnCours
                            ? { label: "EN COURS", cls: "bg-green-100 text-green-700" }
                            : isAVenir
                            ? { label: "À VENIR", cls: "bg-blue-100 text-blue-700" }
                            : { label: "PASSÉE", cls: "bg-gray-100 text-gray-500" };

                          const iconBg = isEnCours ? "bg-green-600" : isAVenir ? "bg-blue-500" : "bg-gray-400";

                          return (
                            <div key={plan.id} className={`bg-white rounded-xl border overflow-hidden ${isEnCours ? "border-green-200" : "border-gray-200"}`}>
                              <div className="p-5">
                                <div className="flex items-center gap-4">
                                  <div className={`w-11 h-11 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                    <Calendar size={20} className="text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-bold text-gray-900 text-base">
                                        {fmtDate(plan.dateDebut)} — {fmtDate(plan.dateFin)}
                                      </span>
                                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${dateBadge.cls}`}>{dateBadge.label}</span>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-0.5">{plan.nombreGardes || 0} pharmacie(s)</p>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {plan.statut === "PUBLIE" ? (
                                      <span className="flex items-center gap-1 px-3 py-1.5 border border-green-300 text-green-700 text-xs font-medium rounded-lg bg-green-50">
                                        <CheckCircle2 size={12} /> Publié
                                      </span>
                                    ) : (
                                      <button onClick={() => publierPlanningComplet(plan.id)}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
                                        <CheckCheck size={12} /><span>Publier</span>
                                      </button>
                                    )}
                                    {["BROUILLON", "PUBLIE"].includes(plan.statut) && (
                                      <button onClick={() => openEditPlanningModal(plan)}
                                        className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50">
                                        ✏️ Modifier
                                      </button>
                                    )}
                                    <button onClick={() => togglePlanningDetail(plan.id)}
                                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Expanded pharmacy grid */}
                              {isExpanded && (
                                <div className="border-t border-gray-100 p-4">
                                  {planningDetailLoading ? (
                                    <div className="text-center py-4"><div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                                  ) : planningDetail?.gardes?.length > 0 ? (() => {
                                    const byPharmacie = {};
                                    planningDetail.gardes.forEach(g => {
                                      const phNom = g.pharmacie?.nom || "—";
                                      const phId = g.pharmacie?.id || g.id;
                                      if (!byPharmacie[phNom]) byPharmacie[phNom] = { nom: phNom, id: phId, commune: g.pharmacie?.commune, gardes: [] };
                                      byPharmacie[phNom].gardes.push(g);
                                    });
                                    return (
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {Object.values(byPharmacie).map(ph => (
                                          <div key={ph.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                              <Building2 size={16} className="text-green-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-semibold text-gray-900 truncate">{ph.nom}</p>
                                              {ph.commune && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><MapPin size={10} />{ph.commune}</p>}
                                            </div>
                                            {["BROUILLON", "PUBLIE"].includes(plan.statut) && (
                                              <button onClick={() => supprimerGarde(ph.gardes[0].id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0">
                                                <Trash2 size={13} />
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  })() : (
                                    <p className="text-center text-sm text-gray-400 py-4">Aucune pharmacie pour ce planning</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Tickets Support */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center space-x-2">
                        <LifeBuoy size={18} className="text-orange-500" /><span>Tickets Support ({tickets.length})</span>
                      </h3>
                      <button onClick={() => setShowTicketForm(true)} className="flex items-center space-x-2 px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600">
                        <Plus size={14} /><span>Nouveau ticket</span>
                      </button>
                    </div>
                    {tickets.length === 0 ? (
                      <p className="text-center text-gray-400 text-sm py-6">Aucun ticket ouvert</p>
                    ) : (
                      <div className="space-y-3">
                        {tickets.map(t => (
                          <div key={t.id} className="p-4 border border-gray-100 rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedTicket(t)}>
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-gray-900 text-sm">{t.sujet}</p>
                              <div className="flex items-center space-x-2">
                                {t.pieceJointeNom && <Paperclip size={12} className="text-gray-400" title={t.pieceJointeNom} />}
                                <TicketBadge statut={t.statut} />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 truncate">{t.message}</p>
                            {t.reponse && (
                              <div className="mt-2 p-2 bg-green-50 rounded text-xs text-green-700 border border-green-100">
                                <span className="font-medium">Réponse: </span>{t.reponse}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}

              {/* ═══ MESSAGERIE ═══════════════════════════════════════════════ */}
              {activeTab === "messages" && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col" style={{ height: "calc(100vh - 180px)" }}>
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">A</div>
                      <div>
                        <p className="font-semibold text-gray-900">Administration SunuFarmasi</p>
                        <div className="flex items-center space-x-1.5">
                          <div className={`w-2 h-2 rounded-full ${chatConnected ? "bg-green-500" : "bg-gray-300"}`} />
                          <span className="text-xs text-gray-500">{chatConnected ? "Connecté" : "Déconnecté"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center py-12">
                        <MessageSquare size={40} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">Commencez la discussion !</p>
                      </div>
                    )}
                    {messages.map((msg, i) => {
                      const isMine = msg.senderId === syndicatId || msg.senderName === userName;
                      return (
                        <div key={i} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                          {!isMine && <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0 mt-1">A</div>}
                          <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl ${isMine ? "bg-green-600 text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                            {!isMine && <p className="text-xs font-medium mb-1 text-green-700">{msg.senderName}</p>}
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isMine ? "text-green-200" : "text-gray-400"}`}>
                              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={chatEndRef} />
                  </div>
                  <form onSubmit={sendMessage} className="p-4 border-t border-gray-200">
                    <div className="flex space-x-3">
                      <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)}
                        placeholder={chatConnected ? "Votre message..." : "Connexion en cours..."}
                        disabled={!chatConnected}
                        className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm disabled:bg-gray-50"
                      />
                      <button type="submit" disabled={!chatConnected || !newMessage.trim()} className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                        <Send size={16} />
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* ═══ SUPPORT ══════════════════════════════════════════════════ */}
              {activeTab === "support" && (
                <SupportTab />
              )}

              {/* ═══ PARAMÈTRES ═══════════════════════════════════════════════ */}
              {activeTab === "parametres" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2"><User size={18} className="text-green-600" /><span>Mon profil</span></h3>
                    {profil ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">{initials}</div>
                          <div><p className="text-xl font-bold text-gray-900">{profil.nom}</p><p className="text-sm text-gray-500 font-mono">{profil.code}</p></div>
                        </div>
                        {[
                          { label: "Username", value: profil.username },
                          { label: "Email", value: profil.contact?.email },
                          { label: "Téléphone", value: profil.contact?.telephone },
                          { label: "Zone", value: profil.zone?.nomCommune || profil.zone?.nomRegion },
                          { label: "Région", value: profil.zone?.nomRegion },
                        ].map(({ label, value }) => (
                          <div key={label} className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-400">{label}</p>
                            <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-gray-400 text-sm">Chargement...</p>}
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2"><Lock size={18} className="text-green-600" /><span>Changer le mot de passe</span></h3>
                    <form onSubmit={changerMotDePasse} className="space-y-4">
                      {[{ k: "ancienMotDePasse", l: "Mot de passe actuel" }, { k: "nouveauMotDePasse", l: "Nouveau mot de passe" }, { k: "confirm", l: "Confirmer le nouveau" }].map(({ k, l }) => (
                        <div key={k}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{l}</label>
                          <input type="password" value={pwForm[k]} onChange={e => setPwForm({ ...pwForm, [k]: e.target.value })} placeholder="••••••••"
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                        </div>
                      ))}
                      {pwMsg && <div className={`p-3 rounded-lg text-sm ${pwMsg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{pwMsg.text}</div>}
                      <button type="submit" disabled={pwLoading} className="w-full py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                        {pwLoading ? "Modification..." : "Modifier le mot de passe"}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {/* ── Footer ── */}
        <footer className="border-t border-gray-200 bg-white px-6 py-4 mt-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield size={14} className="text-white" />
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-700">SunuFarmasi</span>
                <span className="text-gray-400 text-xs ml-2">Syndicat Dashboard · v1.0</span>
              </div>
              <div className="flex items-center gap-1 bg-green-50 border border-green-100 rounded-full px-2.5 py-0.5 ml-1">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-green-600 text-xs font-medium">Actif</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <a href="mailto:sunufarmasi@gmail.com" className="hover:text-green-600 transition-colors flex items-center gap-1">
                <Mail size={12} /> sunufarmasi@gmail.com
              </a>
              <span className="text-gray-200">|</span>
              <a href="tel:+221770000000" className="hover:text-green-600 transition-colors flex items-center gap-1">
                <Phone size={12} /> +221 77 479 24 57
              </a>
              <span className="text-gray-200">|</span>
              <span>© 2025 SunuFarmasi</span>
            </div>
          </div>
        </footer>
      </div>

      {/* ═══ MODALS ══════════════════════════════════════════════════════════ */}

      {showDemandePharmacieModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[92vh]">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Demande d'ajout de pharmacie</h3>
                <p className="text-xs text-gray-500 mt-0.5">Le Super Admin recevra votre demande et validera l'ajout</p>
              </div>
              <button onClick={() => { setShowDemandePharmacieModal(false); setDemandeMsg(null); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={soumettreDemandeAjoutPharmacie} className="p-5 space-y-4 overflow-y-auto flex-1">

              {/* Infos générales */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1.5"><Building2 size={12} />Informations générales</p>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom de la pharmacie *</label>
                  <input type="text" value={demandeForm.nom} onChange={e => setDemandeForm({ ...demandeForm, nom: e.target.value })} placeholder="Ex : Pharmacie du Plateau"
                    required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <textarea value={demandeForm.description} onChange={e => setDemandeForm({ ...demandeForm, description: e.target.value })} rows={2} placeholder="Présentation de la pharmacie..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Services proposés</label>
                  <input type="text" value={demandeForm.services} onChange={e => setDemandeForm({ ...demandeForm, services: e.target.value })} placeholder="Ex : Livraison, garde de nuit, homéopathie..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={demandeForm.accepteCommandes} onChange={e => setDemandeForm({ ...demandeForm, accepteCommandes: e.target.checked })} className="w-4 h-4 accent-green-600" />
                    <span className="text-gray-700">Accepte commandes</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={demandeForm.proposeLivraison} onChange={e => setDemandeForm({ ...demandeForm, proposeLivraison: e.target.checked })} className="w-4 h-4 accent-green-600" />
                    <span className="text-gray-700">Propose livraison</span>
                  </label>
                </div>
              </div>

              {/* Localisation */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1.5"><MapPin size={12} />Localisation</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Commune</label>
                    <input type="text" value={demandeForm.commune} onChange={e => setDemandeForm({ ...demandeForm, commune: e.target.value })} placeholder="Ex : Dakar"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Quartier</label>
                    <input type="text" value={demandeForm.quartier} onChange={e => setDemandeForm({ ...demandeForm, quartier: e.target.value })} placeholder="Ex : Plateau"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Adresse complète</label>
                  <input type="text" value={demandeForm.adresse} onChange={e => setDemandeForm({ ...demandeForm, adresse: e.target.value })} placeholder="N° rue, avenue..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                    <input type="number" step="any" value={demandeForm.latitude} onChange={e => setDemandeForm({ ...demandeForm, latitude: e.target.value })} placeholder="14.6937"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                    <input type="number" step="any" value={demandeForm.longitude} onChange={e => setDemandeForm({ ...demandeForm, longitude: e.target.value })} placeholder="-17.4441"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                {demandeForm.latitude && demandeForm.longitude && (
                  <a href={`https://www.google.com/maps?q=${demandeForm.latitude},${demandeForm.longitude}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
                    <MapPin size={11} /> Voir sur Google Maps
                  </a>
                )}
              </div>

              {/* Contact & Documents */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-3">
                <p className="text-xs font-bold text-gray-700 uppercase">Contact & Documents</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone *</label>
                    <input type="text" value={demandeForm.telephone} onChange={e => setDemandeForm({ ...demandeForm, telephone: e.target.value })} placeholder="+221 77 XXX XX XX"
                      required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                    <input type="email" value={demandeForm.email} onChange={e => setDemandeForm({ ...demandeForm, email: e.target.value })} placeholder="contact@pharmacie.sn"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">N° Ordre pharmacie</label>
                    <input type="text" value={demandeForm.numeroOrdre} onChange={e => setDemandeForm({ ...demandeForm, numeroOrdre: e.target.value })} placeholder="ORD-XXXXX"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">N° Agrément ministère</label>
                    <input type="text" value={demandeForm.numeroAgrement} onChange={e => setDemandeForm({ ...demandeForm, numeroAgrement: e.target.value })} placeholder="AGR-XXXXX"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
              </div>

              {/* Horaires */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <p className="text-xs font-bold text-gray-700 uppercase mb-3">Horaires d'ouverture</p>
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-500"><th className="text-left py-1 w-24">Jour</th><th className="text-center py-1">Ouverture</th><th className="text-center py-1">Fermeture</th><th className="text-center py-1">Fermé</th></tr></thead>
                  <tbody>
                    {SYN_JOURS.map((j, i) => (
                      <tr key={j} className="border-t border-gray-100">
                        <td className="py-1.5 font-medium text-gray-700">{SYN_JOURS_LABELS[i]}</td>
                        <td className="py-1.5 text-center">
                          <input type="time" value={demandeHoraires[j]?.ouverture || ""} disabled={demandeHoraires[j]?.ferme}
                            onChange={e => setDemandeHoraires(h => ({...h, [j]: {...h[j], ouverture: e.target.value}}))}
                            className="px-2 py-1 border border-gray-200 rounded text-xs disabled:opacity-40 disabled:bg-gray-100" />
                        </td>
                        <td className="py-1.5 text-center">
                          <input type="time" value={demandeHoraires[j]?.fermeture || ""} disabled={demandeHoraires[j]?.ferme}
                            onChange={e => setDemandeHoraires(h => ({...h, [j]: {...h[j], fermeture: e.target.value}}))}
                            className="px-2 py-1 border border-gray-200 rounded text-xs disabled:opacity-40 disabled:bg-gray-100" />
                        </td>
                        <td className="py-1.5 text-center">
                          <input type="checkbox" checked={demandeHoraires[j]?.ferme || false}
                            onChange={e => setDemandeHoraires(h => ({...h, [j]: {...h[j], ferme: e.target.checked}}))}
                            className="w-4 h-4 accent-red-500" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pharmacien propriétaire */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 space-y-3">
                <p className="text-xs font-bold text-blue-700 uppercase flex items-center gap-1.5"><User size={12} />Pharmacien propriétaire</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nom *</label>
                    <input type="text" value={demandeForm.pharmacienNom} onChange={e => setDemandeForm({ ...demandeForm, pharmacienNom: e.target.value })} placeholder="DIOP"
                      required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prénom *</label>
                    <input type="text" value={demandeForm.pharmacienPrenom} onChange={e => setDemandeForm({ ...demandeForm, pharmacienPrenom: e.target.value })} placeholder="Moussa"
                      required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Téléphone *</label>
                    <input type="text" value={demandeForm.pharmacienTelephone} onChange={e => setDemandeForm({ ...demandeForm, pharmacienTelephone: e.target.value })} placeholder="+221 77 XXX XX XX"
                      required className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                    <input type="email" value={demandeForm.pharmacienEmail} onChange={e => setDemandeForm({ ...demandeForm, pharmacienEmail: e.target.value })} placeholder="pharmacien@email.sn"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">N° Ordre pharmacien</label>
                  <input type="text" value={demandeForm.pharmacienNumeroOrdre} onChange={e => setDemandeForm({ ...demandeForm, pharmacienNumeroOrdre: e.target.value })} placeholder="ORD-PHARM-XXXXX"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes / informations complémentaires</label>
                <textarea value={demandeForm.message} onChange={e => setDemandeForm({ ...demandeForm, message: e.target.value })} rows={2} placeholder="Informations supplémentaires..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>

              {demandeMsg && <p className={`text-sm text-center font-medium ${demandeMsg.startsWith("Erreur") ? "text-red-600" : "text-green-600"}`}>{demandeMsg}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowDemandePharmacieModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={demandeLoading} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  {demandeLoading ? "Envoi..." : "Envoyer la demande"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTicketForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Nouveau ticket de support</h3>
              <button onClick={() => { setShowTicketForm(false); setTicketFile(null); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={soumettreTicket} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                <input type="text" value={ticketForm.sujet} onChange={e => setTicketForm({ ...ticketForm, sujet: e.target.value })} placeholder="Sujet de votre demande"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select value={ticketForm.priorite} onChange={e => setTicketForm({ ...ticketForm, priorite: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    {["BASSE","NORMALE","HAUTE","URGENTE"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select value={ticketForm.categorie} onChange={e => setTicketForm({ ...ticketForm, categorie: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    {["GENERAL","TECHNIQUE","FACTURATION","PLANNING","AUTRE"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea value={ticketForm.message} onChange={e => setTicketForm({ ...ticketForm, message: e.target.value })} placeholder="Décrivez votre problème..." rows={4}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pièce jointe (optionnelle)</label>
                <label className="flex items-center space-x-2 px-3 py-2.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <Paperclip size={16} className="text-gray-400" />
                  <span className="text-sm text-gray-500 flex-1">{ticketFile ? ticketFile.name : "Choisir un fichier (PDF, image...)"}</span>
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={e => setTicketFile(e.target.files?.[0] || null)} />
                </label>
                {ticketFile && (
                  <button type="button" onClick={() => setTicketFile(null)} className="mt-1 text-xs text-red-500 hover:underline">Supprimer le fichier</button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowTicketForm(false); setTicketFile(null); }} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={ticketSubmitting || fileUploading} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  {fileUploading ? "Upload..." : ticketSubmitting ? "Envoi..." : "Envoyer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedTicket.sujet}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  <TicketBadge statut={selectedTicket.statut} />
                  <span className="text-xs text-gray-400">{selectedTicket.categorie} · {selectedTicket.priorite}</span>
                </div>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg"><p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p></div>
              {selectedTicket.pieceJointeNom && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <FileText size={16} className="text-blue-500" />
                  <span className="text-sm text-blue-700 flex-1">{selectedTicket.pieceJointeNom}</span>
                  {selectedTicket.pieceJointeUrl && (
                    <a href={selectedTicket.pieceJointeUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline flex items-center space-x-1">
                      <Eye size={12} /><span>Voir</span>
                    </a>
                  )}
                </div>
              )}
              {selectedTicket.reponse ? (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <p className="text-xs font-medium text-green-700 mb-2">Réponse de l'administration · {selectedTicket.reponduPar}</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedTicket.reponse}</p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-center">
                  <p className="text-xs text-yellow-700">En attente de réponse de l'administration</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Reset Mot de Passe Compte Associé ─────────────────────────── */}
      {showResetCompteModal && selectedCompte && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Changer le mot de passe</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedCompte.nom} · <span className="font-mono">{selectedCompte.username}</span></p>
              </div>
              <button onClick={() => setShowResetCompteModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleResetComptePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
                <input type="password" value={resetCompteForm.nouveauMotDePasse}
                  onChange={e => setResetCompteForm({ ...resetCompteForm, nouveauMotDePasse: e.target.value })}
                  placeholder="••••••••" required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
                <input type="password" value={resetCompteForm.confirm}
                  onChange={e => setResetCompteForm({ ...resetCompteForm, confirm: e.target.value })}
                  placeholder="••••••••" required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm" />
              </div>
              {resetCompteMsg && (
                <div className={`p-3 rounded-lg text-sm ${resetCompteMsg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                  {resetCompteMsg.text}
                </div>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowResetCompteModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={resetCompteLoading} className="flex-1 py-2.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50">
                  {resetCompteLoading ? "Enregistrement..." : "Changer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Nouvelle Semaine (planning) ──────────────────────────────── */}
      {showCreatePlanningModal && (() => {
        // Auto-calculer prochain samedi et le samedi suivant
        const getProchainSamedi = (offset = 0) => {
          const d = new Date();
          const jour = d.getDay(); // 0=dim, 6=sam
          const daysToSat = (6 - jour + 7) % 7 || 7;
          d.setDate(d.getDate() + daysToSat + offset * 7);
          return d.toISOString().slice(0, 10);
        };
        // Inclure ACTIVE et VALIDEE (VALIDEE = pharmacies créées avant la mise à jour du statut)
        const pharmaciesActives = pharmacies.filter(p => p.statut === "ACTIVE" || p.statut === "VALIDEE");
        const pharmaciesFiltrees = pharmaciesActives;
        const nbSelected = planningSelectedPharmacies.size;
        const togglePharmacie = (id) => {
          const s = new Set(planningSelectedPharmacies);
          s.has(id) ? s.delete(id) : s.add(id);
          setPlanningSelectedPharmacies(s);
        };
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              {/* Header vert */}
              <div className="bg-green-600 rounded-t-2xl p-5 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">Nouvelle semaine de garde</h3>
                  <p className="text-green-200 text-xs mt-0.5">Sélectionnez les pharmacies actives (cotisation à jour)</p>
                </div>
                <button onClick={() => { setShowCreatePlanningModal(false); setPlanningSelectedPharmacies(new Set()); setPlanningFilterVille("all"); }}
                  className="text-white/80 hover:text-white"><X size={22} /></button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 space-y-5">
                {/* Dates avec auto-fill */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Période de garde</label>
                    <button type="button"
                      onClick={() => setPlanningForm({ ...planningForm, dateDebut: getProchainSamedi(0), dateFin: getProchainSamedi(1) })}
                      className="text-xs text-green-600 hover:text-green-700 font-medium underline">
                      Auto : prochain samedi→samedi
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Début (samedi)</label>
                      <input type="date" value={planningForm.dateDebut} onChange={e => setPlanningForm({ ...planningForm, dateDebut: e.target.value })} required
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fin (samedi suivant)</label>
                      <input type="date" value={planningForm.dateFin} onChange={e => setPlanningForm({ ...planningForm, dateFin: e.target.value })} required
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </div>

                {/* Pharmacies selector */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Pharmacies actives ({pharmaciesFiltrees.length}) — {nbSelected} sélectionnée{nbSelected > 1 ? "s" : ""}
                      </p>
                      {pharmacies.filter(p => p.statut === "SUSPENDUE").length > 0 && (
                        <p className="text-xs text-red-500 mt-0.5">
                          {pharmacies.filter(p => p.statut === "SUSPENDUE").length} suspendue(s) non incluse(s)
                        </p>
                      )}
                    </div>
                    <button type="button"
                      onClick={() => {
                        const allIds = new Set(pharmaciesFiltrees.map(p => p.id));
                        nbSelected === pharmaciesFiltrees.length ? setPlanningSelectedPharmacies(new Set()) : setPlanningSelectedPharmacies(allIds);
                      }}
                      className="text-xs text-green-600 hover:text-green-700 font-medium border border-green-300 px-2 py-1 rounded">
                      {nbSelected === pharmaciesFiltrees.length ? "Tout désélect." : "Tout sélect."}
                    </button>
                  </div>

                  {pharmaciesFiltrees.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg">
                      <AlertTriangle size={24} className="text-yellow-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm font-medium">Aucune pharmacie active dans votre réseau</p>
                      <p className="text-gray-400 text-xs mt-1">Activez des pharmacies depuis l'onglet Gestion du réseau</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 max-h-72 overflow-y-auto pr-1">
                      {pharmaciesFiltrees.map(p => {
                        const selected = planningSelectedPharmacies.has(p.id);
                        return (
                          <button key={p.id} type="button" onClick={() => togglePharmacie(p.id)}
                            className={`text-left p-4 rounded-xl border-2 transition-all ${selected ? "border-green-500 bg-green-50" : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"}`}>
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected ? "border-green-500 bg-green-500" : "border-gray-300"}`}>
                                {selected && <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 text-sm truncate">{p.nom}</p>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">{p.pharmacienProprietaireNom || "—"}</p>
                                <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><MapPin size={10} />{p.communeNom || p.quartier || "—"}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer buttons */}
              <div className="p-5 border-t border-gray-100 flex gap-3">
                <button type="button" onClick={() => { setShowCreatePlanningModal(false); setPlanningSelectedPharmacies(new Set()); setPlanningFilterVille("all"); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                  Annuler
                </button>
                <button type="button" onClick={() => creerPlanning(false)} disabled={planningLoading || !planningForm.dateDebut || !planningForm.dateFin}
                  className="flex-1 py-3 bg-gray-700 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {planningLoading ? "..." : <><FileText size={16} />Brouillon</>}
                </button>
                <button type="button" onClick={() => creerPlanning(true)} disabled={planningLoading || !planningForm.dateDebut || !planningForm.dateFin}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {planningLoading ? "..." : <><Send size={16} />Publier</>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal Modifier Pharmacie (réseau syndicat) ───────────────────────── */}
      {showEditPharmacieModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Modifier la pharmacie</h3>
                <p className="text-xs text-gray-500 mt-0.5">Modification des informations de contact</p>
              </div>
              <button onClick={() => setShowEditPharmacieModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={submitEditPharmacieSyndicat} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input type="text" value={editPharmacieData.nom}
                  onChange={e => setEditPharmacieData({ ...editPharmacieData, nom: e.target.value })}
                  placeholder="Nom de la pharmacie"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input type="text" value={editPharmacieData.adresseComplete}
                  onChange={e => setEditPharmacieData({ ...editPharmacieData, adresseComplete: e.target.value })}
                  placeholder="Adresse complète"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="text" value={editPharmacieData.telephone}
                    onChange={e => setEditPharmacieData({ ...editPharmacieData, telephone: e.target.value })}
                    placeholder="+221 77 XXX XX XX"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={editPharmacieData.email}
                    onChange={e => setEditPharmacieData({ ...editPharmacieData, email: e.target.value })}
                    placeholder="contact@pharmacie.sn"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEditPharmacieModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={editPharmacieLoading} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  {editPharmacieLoading ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal Modifier Planning ───────────────────────────────────────────── */}
      {showEditPlanningModal && (() => {
        const pharmaciesActives = pharmacies.filter(p => p.statut === "ACTIVE" || p.statut === "VALIDEE");
        const nbSelected = editPlanningSelectedPharmacies.size;
        const togglePh = (id) => {
          const s = new Set(editPlanningSelectedPharmacies);
          s.has(id) ? s.delete(id) : s.add(id);
          setEditPlanningSelectedPharmacies(s);
        };
        return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-green-600 rounded-t-2xl p-5 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold text-lg">Modifier la semaine de garde</h3>
                <p className="text-green-200 text-xs mt-0.5">{fmtDate(editPlanningData.dateDebut)} → {fmtDate(editPlanningData.dateFin)}</p>
              </div>
              <button onClick={() => setShowEditPlanningModal(false)} className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>

            <form onSubmit={submitEditPlanning} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 overflow-y-auto flex-1 space-y-5">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date début <span className="text-red-500">*</span></label>
                    <input type="date" value={editPlanningData.dateDebut}
                      onChange={e => setEditPlanningData({ ...editPlanningData, dateDebut: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date fin <span className="text-red-500">*</span></label>
                    <input type="date" value={editPlanningData.dateFin}
                      onChange={e => setEditPlanningData({ ...editPlanningData, dateFin: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                  </div>
                </div>

                {/* Titre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre (optionnel)</label>
                  <input type="text" value={editPlanningData.titre}
                    onChange={e => setEditPlanningData({ ...editPlanningData, titre: e.target.value })}
                    placeholder={`Semaine du ${fmtDate(editPlanningData.dateDebut)}`}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
                </div>

                {/* Pharmacies selector */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Pharmacies actives ({pharmaciesActives.length}) — <span className="text-green-600 font-semibold">{nbSelected} sélectionnée{nbSelected > 1 ? "s" : ""}</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Cochez/décochez les pharmacies pour modifier les gardes</p>
                    </div>
                    <button type="button" onClick={() => {
                      const allIds = new Set(pharmaciesActives.map(p => p.id));
                      nbSelected === pharmaciesActives.length
                        ? setEditPlanningSelectedPharmacies(new Set())
                        : setEditPlanningSelectedPharmacies(allIds);
                    }} className="text-xs text-green-600 hover:text-green-700 font-medium border border-green-300 px-2 py-1 rounded">
                      {nbSelected === pharmaciesActives.length ? "Tout désélect." : "Tout sélect."}
                    </button>
                  </div>

                  {editPlanningDetailLoading ? (
                    <div className="text-center py-6"><div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                  ) : pharmaciesActives.length === 0 ? (
                    <p className="text-center text-gray-400 text-sm py-4">Aucune pharmacie active</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                      {pharmaciesActives.map(p => {
                        const selected = editPlanningSelectedPharmacies.has(p.id);
                        return (
                          <button key={p.id} type="button" onClick={() => togglePh(p.id)}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${selected ? "border-green-500 bg-green-50" : "border-gray-200 bg-white hover:border-gray-300"}`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selected ? "border-green-500 bg-green-500" : "border-gray-300"}`}>
                                {selected && <svg width="10" height="8" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{p.nom}</p>
                                <p className="text-xs text-gray-400 truncate"><MapPin size={9} className="inline mr-0.5" />{p.communeNom || "—"}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-gray-100 flex gap-3">
                <button type="button" onClick={() => setShowEditPlanningModal(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50">
                  Annuler
                </button>
                <button type="submit" disabled={editPlanningLoading}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  {editPlanningLoading ? "Enregistrement..." : <><CheckCheck size={16} />Enregistrer les modifications</>}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}

      {/* ── Modal Ajouter Garde ──────────────────────────────────────────────── */}
      {showAddGardeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Ajouter une garde</h3>
              <button onClick={() => setShowAddGardeModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={ajouterGarde} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pharmacie *</label>
                <select value={gardeForm.pharmacieId} onChange={e => setGardeForm({ ...gardeForm, pharmacieId: e.target.value })} required
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">— Sélectionner une pharmacie —</option>
                  {pharmacies.filter(p => p.statut === "ACTIVE" || p.statut === "VALIDEE").map(p => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date garde *</label>
                  <input type="date" value={gardeForm.dateGarde} onChange={e => setGardeForm({ ...gardeForm, dateGarde: e.target.value })} required
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={gardeForm.typeGarde} onChange={e => setGardeForm({ ...gardeForm, typeGarde: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    {["JOURNEE","NUIT","24H","WEEK_END","FERIE"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                  <input type="time" value={gardeForm.heureDebut} onChange={e => setGardeForm({ ...gardeForm, heureDebut: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                  <input type="time" value={gardeForm.heureFin} onChange={e => setGardeForm({ ...gardeForm, heureFin: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <input type="text" value={gardeForm.notes} onChange={e => setGardeForm({ ...gardeForm, notes: e.target.value })} placeholder="Optionnel..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowAddGardeModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={gardeLoading} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  {gardeLoading ? "Ajout..." : "Ajouter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Composant Support ─────────────────────────────────────────────────────────

function SupportTab() {
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ sujet: "", message: "", priorite: "NORMALE", categorie: "AUTRE" });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    setLoading(true);
    ticketApi.getMesTickets().then(data => setTickets(Array.isArray(data) ? data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.sujet.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await ticketApi.create(form);
      setForm({ sujet: "", message: "", priorite: "NORMALE", categorie: "AUTRE" });
      const data = await ticketApi.getMesTickets();
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      alert("Erreur envoi ticket : " + (err.message || "inconnue"));
    } finally {
      setSending(false);
    }
  };

  const faq = [
    { q: "Comment publier un planning de garde ?", a: "Dans l'onglet Planning, créez un planning, ajoutez les pharmacies via les cases à cocher, puis cliquez sur Publier. Le planning sera immédiatement visible dans l'application mobile." },
    { q: "Comment ajouter une pharmacie à mon réseau ?", a: "Dans l'onglet Réseau, cliquez sur « Ajouter une pharmacie ». Remplissez les informations et soumettez — la pharmacie sera en statut EN_ATTENTE jusqu'à validation." },
    { q: "Comment créer un sous-compte pour mon syndicat ?", a: "Contactez l'administrateur via ce formulaire de support en précisant le nom et le rôle du sous-compte souhaité." },
    { q: "Pourquoi mes pharmacies n'apparaissent pas dans les gardes ?", a: "Seules les pharmacies au statut ACTIVE peuvent participer aux gardes. Vérifiez le statut de vos pharmacies dans l'onglet Réseau. Si elles sont VALIDEES, contactez l'admin pour activation." },
    { q: "Comment réinitialiser le mot de passe d'un sous-compte ?", a: "Dans l'onglet Réseau → Comptes associés, cliquez sur le bouton « Changer mdp » en face du compte concerné." },
    { q: "Mon planning a été publié mais les gardes restent PLANIFIÉE ?", a: "Si le planning couvre la semaine en cours, les gardes passent automatiquement EN_COURS à la publication. Si le problème persiste, contactez le support." },
  ];

  const prioriteColor = { "URGENTE": "red", "HAUTE": "orange", "NORMALE": "blue", "BASSE": "gray" };

  return (
    <div className="space-y-6">
      {/* Contact info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
          <div className="w-11 h-11 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Phone size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Téléphone support</p>
            <p className="font-semibold text-gray-900">+221 77 479 24 57</p>
            <p className="text-xs text-gray-400">Lun–Ven · 8h–18h</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
          <div className="w-11 h-11 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Mail size={20} className="text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Email support</p>
            <p className="font-semibold text-gray-900">sunufarmasi@gmail.com</p>
            <p className="text-xs text-gray-400">Réponse sous 24h</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
          <div className="w-11 h-11 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileText size={20} className="text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Mes tickets</p>
            <p className="font-semibold text-gray-900">{tickets.length} ticket{tickets.length !== 1 ? "s" : ""}</p>
            <p className="text-xs text-gray-400">{tickets.filter(t => t.statut === "OUVERT" || t.statut === "EN_COURS").length} en cours</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulaire ticket */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Send size={16} className="text-green-600" />Nouveau ticket de support</h3>
          </div>
          <form onSubmit={handleSend} className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sujet *</label>
              <input type="text" value={form.sujet} onChange={e => setForm({...form, sujet: e.target.value})} required
                placeholder="Décrivez brièvement votre problème"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie</label>
                <select value={form.categorie} onChange={e => setForm({...form, categorie: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="TECHNIQUE">Problème technique</option>
                  <option value="FACTURATION">Facturation</option>
                  <option value="PLANNING">Planning de garde</option>
                  <option value="PHARMACIE">Pharmacie</option>
                  <option value="COMPTE">Compte</option>
                  <option value="AUTRE">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Priorité</label>
                <select value={form.priorite} onChange={e => setForm({...form, priorite: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="BASSE">Basse</option>
                  <option value="NORMALE">Normale</option>
                  <option value="HAUTE">Haute</option>
                  <option value="URGENTE">Urgente</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Message *</label>
              <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} required rows={5}
                placeholder="Décrivez votre problème en détail..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"/>
            </div>
            <button type="submit" disabled={sending}
              className="w-full py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {sending ? <><RefreshCw size={14} className="animate-spin"/>Envoi...</> : <><Send size={14}/>Envoyer le ticket</>}
            </button>
          </form>
        </div>

        {/* Mes tickets */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardList size={16} className="text-blue-600" />Mes tickets</h3>
            <button onClick={() => ticketApi.getMesTickets().then(d => setTickets(Array.isArray(d) ? d : [])).catch(() => {})}
              className="text-gray-400 hover:text-gray-600"><RefreshCw size={14}/></button>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loading ? (
              <p className="p-8 text-center text-gray-400 text-sm">Chargement...</p>
            ) : tickets.length === 0 ? (
              <p className="p-8 text-center text-gray-400 text-sm">Aucun ticket pour l'instant</p>
            ) : (
              tickets.map(t => (
                <div key={t.id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-medium text-gray-800 text-sm truncate flex-1">{t.sujet}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                      t.statut === "RESOLU" || t.statut === "FERME" ? "bg-green-100 text-green-700" :
                      t.statut === "EN_COURS" ? "bg-yellow-100 text-yellow-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>{t.statut || "OUVERT"}</span>
                  </div>
                  <p className="text-xs text-gray-400">{t.createdAt ? new Date(t.createdAt).toLocaleDateString("fr-FR") : ""}</p>
                  {t.reponse && (
                    <div className="mt-2 bg-green-50 rounded-lg p-2.5 border border-green-100">
                      <p className="text-xs text-green-600 font-medium mb-0.5">Réponse admin</p>
                      <p className="text-xs text-gray-600">{t.reponse}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><AlertCircle size={16} className="text-orange-500" />Foire aux questions (FAQ)</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {faq.map((item, i) => (
            <div key={i} className="p-4">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between text-left gap-3">
                <p className="font-medium text-gray-800 text-sm">{item.q}</p>
                {openFaq === i ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0"/> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0"/>}
              </button>
              {openFaq === i && (
                <p className="mt-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
